import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { Reveal } from '@/components/anim'
import { footerColumns } from '@/constants/navigation'

// Inline social icons (outline style) — avoids pulling in the large
// lucide-react package for just four glyphs.
const socialPaths: Record<string, ReactNode> = {
    Twitter: (
        <path d='M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z' />
    ),
    Facebook: (
        <path d='M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z' />
    ),
    YouTube: (
        <>
            <path d='M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17' />
            <path d='m10 15 5-3-5-3z' />
        </>
    ),
    LinkedIn: (
        <>
            <path d='M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z' />
            <rect width='4' height='12' x='2' y='9' />
            <circle cx='4' cy='4' r='2' />
        </>
    )
}

const SocialIcon: FC<{ label: string }> = ({ label }) => (
    <svg
        className='social-media-icon'
        width={18}
        height={18}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth={1.75}
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
    >
        {socialPaths[label]}
    </svg>
)

const footerSocials = ['Twitter', 'Facebook', 'YouTube', 'LinkedIn']

const FooterLink: FC<{ href: string; external?: boolean; children: ReactNode }> = ({
    href,
    external,
    children
}) => {
    const isRoute = href.startsWith('/') && !href.startsWith('/#')

    if (isRoute && !external) {
        return (
            <Link href={href} className='footer-link w-inline-block'>
                <div>{children}</div>
            </Link>
        )
    }

    return (
        <a
            href={href}
            className='footer-link w-inline-block'
            {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
            <div>{children}</div>
        </a>
    )
}

export const Footer: FC = (): ReactNode => {
    const year = new Date().getFullYear()

    return (
        <div className='footer'>
            <div className='container footer-container'>
                <div className='footer-grid'>
                    <div className='footer-content'>
                        <Reveal className='fade-in-move-on-scroll'>
                            <div className='footer-brand-content-holder'>
                                <h4 className='no-margin-top'>Empowering Dreams, One Dollar at a Time</h4>
                                <a href='mailto:hello@modulify.ai' className='email-container w-inline-block'>
                                    <div className='link-button-with-icon'>
                                        <img src='/images/Arrow-right.svg' loading='lazy' alt='' className='arrow-right-image' />
                                    </div>
                                    <div className='footer-email-text'>hello@modulify.ai</div>
                                </a>
                                <div className='footer-social-media'>
                                    {footerSocials.map((label) => (
                                        <a
                                            key={label}
                                            href='#'
                                            target='_blank'
                                            rel='noreferrer'
                                            aria-label={label}
                                            className='social-media-link w-inline-block'
                                        >
                                            <SocialIcon label={label} />
                                            <div className='link-background' />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <div className='footer-content-holder'>
                        {footerColumns.map((column) => (
                            <div className='footer-grid-content' key={column.heading}>
                                <Reveal className='footer-heading'>{column.heading}</Reveal>
                                {column.links.map((link) => (
                                    <Reveal className='fade-in-move-on-scroll' key={link.label}>
                                        <FooterLink href={link.href} external={link.external}>
                                            {link.label}
                                        </FooterLink>
                                    </Reveal>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                <div className='made-by-section'>
                    <Link href='/' className='footer-logo-link' aria-label='Manila home'>
                        <img src='/images/Logo.svg' alt='Manila' className='footer-image' />
                    </Link>
                    <div className='copyright-text'>
                        <div>© {year} Manila. All rights reserved.</div>
                    </div>
                </div>
            </div>
        </div>
    )
}
